/*
 * Wishlist — v1 (ported from CarMoods)
 * Guest-friendly wishlist stored in localStorage. No app, no account required.
 *
 * Public API:
 *   window.CarmoodsWishlist.read()          -> ["handle-a", "handle-b"]
 *   window.CarmoodsWishlist.has(handle)     -> boolean
 *   window.CarmoodsWishlist.toggle(handle)  -> boolean (new state)
 *   window.CarmoodsWishlist.count()         -> number
 *   document.addEventListener('carmoods:wishlist:change', fn)
 *
 * The carmoods:* names are kept deliberately: this file is a verbatim port and
 * staying byte-comparable with the original matters more than the names, which
 * are never visible to shoppers. Storage is per-origin, so nothing is shared.
 *
 * Custom elements: <wishlist-button>, <wishlist-count>, <wishlist-page>
 */
(function () {
  'use strict';

  var KEY = 'carmoods:wishlist:v1';
  var EVT = 'carmoods:wishlist:change';
  var MAX_ITEMS = 200;

  /* i18n strings from sections/wishlist-assets.liquid. Declared here (rather
     than beside CONFIG further below) because <wishlist-count> can already be
     present in the server-rendered header markup: customElements.define()
     upgrades it synchronously and calls connectedCallback -> sync() before
     the rest of this script has run, so T must exist before any class body
     that reads it. CONFIG.i18n resolves to the same object once it exists. */
  var T = (window.CARMOODS_WISHLIST_CONFIG && window.CARMOODS_WISHLIST_CONFIG.i18n) || {};
  function plural(n, one, other, fallbackOne, fallbackOther) {
    var s = n === 1 ? (one || fallbackOne) : (other || fallbackOther);
    return String(s).replace('%%COUNT%%', n);
  }

  /* ---------------------------------------------------------------- store */

  var Wishlist = {
    read: function () {
      try {
        var raw = window.localStorage.getItem(KEY);
        if (!raw) return [];
        var parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(function (h) {
          return typeof h === 'string' && h.length > 0;
        });
      } catch (e) {
        return [];
      }
    },

    write: function (list) {
      var trimmed = list.slice(0, MAX_ITEMS);
      try {
        window.localStorage.setItem(KEY, JSON.stringify(trimmed));
      } catch (e) {
        /* private mode / quota — wishlist just won't persist */
      }
      document.dispatchEvent(new CustomEvent(EVT, { detail: { items: trimmed } }));
      return trimmed;
    },

    has: function (handle) {
      return this.read().indexOf(handle) !== -1;
    },

    add: function (handle) {
      var list = this.read();
      if (list.indexOf(handle) === -1) {
        list.unshift(handle);
        this.write(list);
      }
      return true;
    },

    remove: function (handle) {
      this.write(
        this.read().filter(function (h) {
          return h !== handle;
        })
      );
      return false;
    },

    toggle: function (handle) {
      return this.has(handle) ? this.remove(handle) : this.add(handle);
    },

    count: function () {
      return this.read().length;
    }
  };

  window.CarmoodsWishlist = Wishlist;

  /* keep tabs in sync */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) {
      document.dispatchEvent(new CustomEvent(EVT, { detail: { items: Wishlist.read() } }));
    }
  });

  /* ------------------------------------------------------------- helpers */

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function rootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  /* ---------------------------------------------------- <wishlist-button> */

  class WishlistButtonElement extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.button = this.querySelector('button');
      if (!this.handle || !this.button) return;

      /* No per-element document listener: a single delegated listener syncs
         every instance. On a 40-card collection page that's 1 listener
         instead of 40, and nothing to clean up on disconnect. */
      this.button.addEventListener('click', this.onClick.bind(this));
      this.sync();
    }

    onClick(event) {
      /* product cards wrap the whole tile in a link overlay — don't navigate */
      event.preventDefault();
      event.stopPropagation();

      Wishlist.toggle(this.handle);

      this.button.classList.remove('wishlist-button__btn--bump');
      void this.button.offsetWidth;
      this.button.classList.add('wishlist-button__btn--bump');
    }

    sync() {
      var active = Wishlist.has(this.handle);
      this.classList.toggle('wishlist-button--active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.button.setAttribute(
        'aria-label',
        active
          ? this.dataset.labelRemove || T.remove || 'Remove from wishlist'
          : this.dataset.labelAdd || T.add || 'Add to wishlist'
      );

      /* the product-page variant has a visible text label */
      var label = this.querySelector('[data-wishlist-label]');
      if (label) {
        label.textContent = active
          ? this.dataset.textRemove || T.saved || 'Saved to wishlist'
          : this.dataset.textAdd || T.save || 'Save to wishlist';
      }
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButtonElement);
  }

  /* ----------------------------------------------------- <wishlist-count> */

  class WishlistCountElement extends HTMLElement {
    connectedCallback() {
      this.sync();
    }

    sync() {
      var count = Wishlist.count();
      var bubble = this.querySelector('[data-wishlist-bubble]');
      var number = this.querySelector('[data-wishlist-count]');
      var text = this.querySelector('[data-wishlist-count-text]');

      if (number) number.textContent = count > 99 ? '99+' : String(count);
      if (text) text.textContent = plural(count, T.itemsInWishlistOne, T.itemsInWishlistOther, '1 item in wishlist', count + ' items in wishlist');

      if (bubble) {
        /* visibility is driven by a class we own, never by [hidden] — the
           theme's own badge rules use display:flex, which beats [hidden] */
        bubble.removeAttribute('hidden');
        bubble.classList.toggle('wishlist-badge--visible', count > 0);
      }

      this.classList.toggle('wishlist-count--empty', count === 0);
    }
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', WishlistCountElement);
  }

  /* One delegated listener keeps every hearts/badge in sync, including
     elements created by the inline primer in wishlist-assets.liquid and any
     the theme re-renders later. */
  function syncAll() {
    var els = document.querySelectorAll('wishlist-button, wishlist-count');
    for (var i = 0; i < els.length; i++) {
      if (typeof els[i].sync === 'function') els[i].sync();
    }
  }

  document.addEventListener(EVT, syncAll);
  window.addEventListener('pageshow', syncAll); /* back/forward bfcache restore */

  /* ------------------------------------------------------ <wishlist-page> */

  class WishlistPageElement extends HTMLElement {
    connectedCallback() {
      this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.loadingState = this.querySelector('[data-wishlist-loading]');
      this.summary = this.querySelector('[data-wishlist-summary]');
      this.summaryText = this.querySelector('[data-wishlist-summary-text]');
      this.clearButton = this.querySelector('[data-wishlist-clear]');
      this.cache = {};

      this._render = this.render.bind(this);
      document.addEventListener(EVT, this._render);
      this.addEventListener('click', this.onClick.bind(this));
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(EVT, this._render);
    }

    setState(state) {
      if (this.loadingState) this.loadingState.hidden = state !== 'loading';
      if (this.emptyState) this.emptyState.hidden = state !== 'empty';
      if (this.grid) this.grid.hidden = state !== 'ready';
      if (this.clearButton) this.clearButton.hidden = state !== 'ready';
    }

    setSummary(count) {
      if (this.summary) this.summary.textContent = count ? ' (' + count + ')' : '';
      if (this.summaryText) {
        this.summaryText.textContent = count
          ? plural(count, T.itemsSavedOne, T.itemsSavedOther, '1 item saved', count + ' items saved')
          : '';
      }
    }

    async render() {
      var self = this;
      var handles = Wishlist.read();

      this.setSummary(handles.length);

      if (!handles.length) {
        if (this.grid) this.grid.innerHTML = '';
        this.setState('empty');
        return;
      }

      if (!this.grid.children.length) this.setState('loading');

      var results = await Promise.all(
        handles.map(function (handle) {
          return self.fetchCardHtml(handle);
        })
      );

      /* null === confirmed gone (deleted/unpublished); undefined === fetch failed */
      var missing = [];
      var found = [];
      results.forEach(function (html, i) {
        if (html) found.push({ handle: handles[i], html: html });
        else if (html === null) missing.push(handles[i]);
      });

      if (missing.length) {
        Wishlist.write(
          handles.filter(function (h) {
            return missing.indexOf(h) === -1;
          })
        );
        return; /* the write re-triggers render */
      }

      if (!found.length) {
        this.setState('empty');
        return;
      }

      this.renderCards(found);
      this.setState('ready');
    }

    /* Fetches the real theme card markup via the Section Rendering API so
       wishlist cards are pixel-identical to collection-page cards (review
       stars, price formatting, alignment — all inherited, nothing hand-built
       here to drift out of sync). See sections/wishlist-card.liquid. */
    async fetchCardHtml(handle) {
      if (Object.prototype.hasOwnProperty.call(this.cache, handle)) return this.cache[handle];
      try {
        var res = await fetch(
          rootUrl() + 'products/' + encodeURIComponent(handle) + '?section_id=wishlist-card'
        );
        if (!res.ok) {
          this.cache[handle] = null;
          return null;
        }
        var html = await res.text();
        this.cache[handle] = html;
        return html;
      } catch (e) {
        return undefined; /* network hiccup — keep the item, skip this pass */
      }
    }

    /* Wraps each fetched card in a <li> that owns the remove (X) control and
       the removing animation, without touching the card markup itself. */
    renderCards(items) {
      var frag = document.createDocumentFragment();

      items.forEach(function (item) {
        var li = document.createElement('li');
        li.className = 'wishlist-card grid__item';
        li.setAttribute('data-handle', item.handle);

        var temp = document.createElement('div');
        temp.innerHTML = item.html;
        while (temp.firstChild) {
          li.appendChild(temp.firstChild);
        }

        var headingLink = li.querySelector('.card__heading a');
        var title = headingLink ? headingLink.textContent.replace(/\s+/g, ' ').trim() : item.handle;

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'wishlist-card__remove';
        removeBtn.setAttribute('data-wishlist-remove', item.handle);
        removeBtn.setAttribute(
          'aria-label',
          T.removeNamed
            ? T.removeNamed.replace('%%TITLE%%', title)
            : 'Remove ' + title + ' from wishlist'
        );
        removeBtn.innerHTML =
          '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">' +
          '<path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
        li.insertBefore(removeBtn, li.firstChild);

        frag.appendChild(li);
      });

      this.grid.innerHTML = '';
      this.grid.appendChild(frag);

      renderReviewBadges();
    }

    onClick(event) {
      if (event.target.closest('[data-wishlist-clear]')) {
        event.preventDefault();
        if (window.confirm(T.clearConfirm || 'Remove all items from your wishlist?')) {
          Wishlist.write([]);
        }
        return;
      }

      var removeBtn = event.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        event.preventDefault();
        var card = removeBtn.closest('.wishlist-card');
        if (card) card.classList.add('wishlist-card--removing');
        Wishlist.remove(removeBtn.getAttribute('data-wishlist-remove'));
        return;
      }

      /* Superseded by the real add-to-cart form each fetched card now
         includes (see sections/wishlist-card.liquid), but left in place —
         harmless if nothing matches [data-wishlist-add] any more. */
      var addBtn = event.target.closest('[data-wishlist-add]');
      if (addBtn) {
        event.preventDefault();
        this.addToCart(addBtn);
      }
    }

    async addToCart(button) {
      var label = button.querySelector('[data-label]') || button;
      var original = label.textContent;
      button.disabled = true;
      label.textContent = T.adding || 'Adding…';

      var restore = function (message, delay) {
        label.textContent = message;
        setTimeout(function () {
          label.textContent = original;
          button.disabled = false;
        }, delay);
      };

      try {
        var res = await fetch(this.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items: [{ id: Number(button.getAttribute('data-wishlist-add')), quantity: 1 }],
            sections: 'cart-icon-bubble'
          })
        });

        var data = await res.json();

        if (!res.ok) {
          restore(data.description || T.unavailable || 'Unavailable', 2500);
          return;
        }

        if (data.sections && data.sections['cart-icon-bubble']) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            var parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
            var fresh = parsed.getElementById('cart-icon-bubble') || parsed.querySelector('.shopify-section');
            if (fresh) bubble.innerHTML = fresh.innerHTML;
          }
        }

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        button.classList.add('wishlist-card__action--added');
        label.textContent = (T.added || 'Added') + ' ✓';
        setTimeout(function () {
          button.classList.remove('wishlist-card__action--added');
          label.textContent = original;
          button.disabled = false;
        }, 2000);
      } catch (e) {
        restore(T.tryAgain || 'Try again', 2500);
      }
    }
  }

  if (!customElements.get('wishlist-page')) {
    customElements.define('wishlist-page', WishlistPageElement);
  }

  /* ------------------------------------------------------------- injector
   * The header and product cards are heavily customised, so instead of
   * editing those large Liquid files we inject the heart and the header icon
   * at runtime. If you later paste {% render 'wishlist-button' %} into
   * card-product.liquid or {% render 'wishlist-icon' %} into the header, the
   * injector detects them and stands down — no duplicates.
   * -------------------------------------------------------------------- */

  var CONFIG = Object.assign(
    {
      cardSelector: '.card-wrapper, .product-card, [data-product-card]',
      headerActionsSelector: '.h-actions',
      cartLinkSelector: 'a[href*="/cart"]',
      productButtonsSelector: '.product-form__buttons',
      enableCardInjection: true,
      enableHeaderInjection: true,
      enableProductButton: true
    },
    window.CARMOODS_WISHLIST_CONFIG || {}
  );

  var HEART_SVG =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
    '<path class="wishlist-icon__outline" d="M12 20.3l-1.16-1.05C6.72 15.55 4 13.09 4 10.06 4 7.6 5.94 5.7 8.4 5.7c1.39 0 2.72.64 3.6 1.66a4.77 4.77 0 0 1 3.6-1.66c2.46 0 4.4 1.9 4.4 4.36 0 3.03-2.72 5.49-6.84 9.2L12 20.3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
    '<path class="wishlist-icon__fill" d="M12 20.3l-1.16-1.05C6.72 15.55 4 13.09 4 10.06 4 7.6 5.94 5.7 8.4 5.7c1.39 0 2.72.64 3.6 1.66a4.77 4.77 0 0 1 3.6-1.66c2.46 0 4.4 1.9 4.4 4.36 0 3.03-2.72 5.49-6.84 9.2L12 20.3z" fill="currentColor"/>' +
    '</svg>';

  function handleFromCard(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var match = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function injectCards(root) {
    if (!CONFIG.enableCardInjection) return;
    var scope = root && root.querySelectorAll ? root : document;
    var cards = scope.querySelectorAll(CONFIG.cardSelector);

    Array.prototype.forEach.call(cards, function (card) {
      if (card.querySelector('wishlist-button')) return; /* already there */

      /* the wishlist page renders real theme cards with their own X remove
         control (see WishlistPageElement.renderCards) — a heart here would
         sit right under it. */
      if (card.closest('wishlist-page')) return;

      var handle = handleFromCard(card);
      if (!handle) return;

      if (window.getComputedStyle(card).position === 'static') {
        card.style.position = 'relative';
      }

      var el = document.createElement('wishlist-button');
      el.className = 'wishlist-button';
      el.setAttribute('data-handle', handle);
      el.innerHTML =
        '<button type="button" class="wishlist-button__btn" aria-pressed="false" aria-label="' + escapeHtml(T.add || 'Add to wishlist') + '">' +
        HEART_SVG +
        '</button>';
      card.appendChild(el);
    });
  }

  function injectHeader() {
    if (!CONFIG.enableHeaderInjection) return;
    if (document.getElementById('wishlist-icon-bubble')) return; /* Liquid version present */

    var groups = document.querySelectorAll(CONFIG.headerActionsSelector);

    Array.prototype.forEach.call(groups, function (actions) {
      if (actions.querySelector('wishlist-count')) return;

      var el = document.createElement('wishlist-count');
      el.className = 'wishlist-count';
      el.innerHTML =
        '<a href="' +
        rootUrl() +
        'pages/wishlist" class="h-icon-btn h-wishlist-btn eco-act eco-act--wishlist" aria-label="Wishlist">' +
        '<svg viewBox="0 0 24 24" fill="none" width="22" height="22" aria-hidden="true" focusable="false">' +
        '<path d="M12 20.3l-1.16-1.05C6.72 15.55 4 13.09 4 10.06 4 7.6 5.94 5.7 8.4 5.7c1.39 0 2.72.64 3.6 1.66a4.77 4.77 0 0 1 3.6-1.66c2.46 0 4.4 1.9 4.4 4.36 0 3.03-2.72 5.49-6.84 9.2L12 20.3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>' +
        '<span class="wishlist-badge" data-wishlist-bubble><span aria-hidden="true" data-wishlist-count>0</span></span>' +
        '<span class="visually-hidden" data-wishlist-count-text>0 items in wishlist</span>' +
        '</a>';

      var cartLink = actions.querySelector(CONFIG.cartLinkSelector);
      if (cartLink) actions.insertBefore(el, cartLink);
      else actions.appendChild(el);
    });
  }

  function currentProductHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* Full-width "Save to wishlist" button under Add to cart / Buy it now. */
  function injectProductButton() {
    if (!CONFIG.enableProductButton) return;

    var handle = currentProductHandle();
    if (!handle) return; /* only on a product page */

    var groups = document.querySelectorAll(CONFIG.productButtonsSelector);

    Array.prototype.forEach.call(groups, function (buttons) {
      if (buttons.querySelector('wishlist-button')) return;

      /* quick-add modals and card forms reuse this class — those are other
         products, and the page handle would be wrong for them */
      if (buttons.closest('.card-wrapper, quick-add-modal, modal-dialog, .quick-add, .quick-add-modal')) {
        return;
      }

      var el = document.createElement('wishlist-button');
      el.className = 'wishlist-button wishlist-button--inline';
      el.setAttribute('data-handle', handle);
      el.innerHTML =
        '<button type="button" class="wishlist-button__btn wishlist-button__btn--inline button button--secondary button--full-width" aria-pressed="false" aria-label="' + escapeHtml(T.add || 'Add to wishlist') + '">' +
        HEART_SVG +
        '<span data-wishlist-label>' + escapeHtml(T.save || 'Save to wishlist') + '</span>' +
        '</button>';
      buttons.appendChild(el);
    });
  }

  /* Judge.me renders review stars into placeholder elements on script load
     and has no idea about cards injected afterwards, so wishlist cards need
     a manual nudge once they land in the DOM. Different Judge.me builds
     expose different re-render entry points, so we probe for whichever is
     actually present rather than assuming one. */
  function renderReviewBadges() {
    try {
      if (!window.jdgm) return;
      if (typeof window.jdgm.customizeBadges === 'function') {
        window.jdgm.customizeBadges();
      } else if (typeof window.jdgm.batchRenderBadges === 'function') {
        window.jdgm.batchRenderBadges();
      } else if (typeof window.jdgm.updateBadges === 'function') {
        window.jdgm.updateBadges();
      }
    } catch (e) {}
  }

  var timer = null;

  /* Each injector is isolated: a third-party script breaking one of them
     must not stop the other two from running. */
  function injectAll(root) {
    try {
      injectHeader();
    } catch (e) {}
    try {
      injectCards(root);
    } catch (e) {}
    try {
      injectProductButton();
    } catch (e) {}
  }

  function scheduleInject() {
    if (timer) return;
    timer = window.setTimeout(function () {
      timer = null;
      injectAll();
    }, 150);
  }

  function start() {
    injectAll();

    /* Collection filters, quick-add and infinite scroll swap grid markup.
       Debounced to 150ms, and mutations we caused ourselves are ignored so
       injection can't feed itself in a loop. */
    if (window.MutationObserver && document.body) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (!m.addedNodes || !m.addedNodes.length) continue;

          var target = m.target;
          if (
            target &&
            target.closest &&
            target.closest('wishlist-button, wishlist-count, wishlist-page')
          ) {
            continue; /* our own DOM writes */
          }

          scheduleInject();
          return;
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    /* theme editor */
    document.addEventListener('shopify:section:load', function (e) {
      injectAll(e.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
